import React, { useCallback, useEffect, useState } from 'react';

import { isUUIDV4, Select } from '@aline/common';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import {
  ButtonBase,
  Divider,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColumnMenuContainer,
  gridFilterModelSelector,
  gridPinnedColumnsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import type {
  GridApiPremium,
  GridColDef,
  GridColumnMenuProps,
  GridFilterModel,
} from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { ChevronDown, EyeOff, Pin, PinOff, Sparkles, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AlineTextarea } from 'src/components/AlineTextarea';
import { AlineButton, AlineTooltip, Spacer } from 'src/components/generic';
import {
  COLUMN_LOCK_EMPTY_LABEL,
  COLUMN_LOCK_LABEL,
  ExtractionQuestionInputsPopper,
  getColumnSpecificOpts,
  getLockAction,
  LockIcon,
  QuestionInputChips,
  questionTypeToReportColumnDataType,
  useColumnLockState,
  useLockConfirmation,
} from 'src/components/reports';
import type { LockableJob, LockAction } from 'src/components/reports';
import {
  ExtractionQuestionOptionsManager,
  ExtractionQuestionTypeIcon,
  ExtractionQuestionTypePopper,
} from 'src/editor';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { doNothing, useReportParams } from 'src/helpers';
import {
  getVisibleDocumentIds,
  patchExtractionColumnOptionInCache,
  useDeleteExtractionQuestion,
  useGetExtractionQuestionWithInputs,
  useRunExtractionJobsForDocuments,
  useSetQuestionInputsOptimistic,
  useStartReportExtraction,
  useViewportClamp,
  useVisibleDocumentIds,
} from 'src/hooks';
import { Sentry } from 'src/sentry';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';

import { ExtractionModePopper } from './ExtractionModePopper';

const QUESTION_HEADER_ICON_SIZE = 18;
const QUESTION_HEADER_CHEVRON_SIZE = 16;
const DELETE_PROPERTY_ICON_SIZE = 40;

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
  deleteIcon: {
    marginRight: theme.spacing(2),
    minWidth: 'unset',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    width: 400,
  },
  pinButton: {
    opacity: 0,
    transition: 'opacity 0.2s, background-color 0.2s',
  },
  pinButtonPinned: {
    '&:hover': {
      backgroundColor: theme.palette.grey[900],
    },
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    opacity: 1,
  },
  skeletonButton: {
    borderRadius: 16,
  },
  skeletonMenuButton: {
    alignItems: 'baseline',
    borderRadius: 8,
    display: 'flex',
    flexGrow: 1,
    height: 32,
    justifyContent: 'left',
    padding: theme.spacing(0, 1),
  },
  sparkleIcon: {
    marginRight: theme.spacing(1),
  },
}));

export type ColumnHeaderMenuExtraProps = {
  popperAnchor?: 'left-start' | 'right-start';
};
type ColumnHeaderMenuProps = GridColumnMenuProps & ColumnHeaderMenuExtraProps;

export function ColumnHeaderMenu(props: ColumnHeaderMenuProps): JSX.Element {
  const { colDef: currentColumn } = props;
  const isAIField = isUUIDV4(currentColumn.field);
  const { maxHeight, setNode } = useViewportClamp<HTMLUListElement>();

  if (isAIField)
    return (
      <GridColumnMenuContainer
        ref={setNode}
        style={{ maxHeight, overflowY: 'auto', paddingBottom: 0, paddingTop: 0 }}
        {...props}
      >
        <QuestionColumnHeaderMenu {...props} />
      </GridColumnMenuContainer>
    );

  return (
    <GridColumnMenuContainer ref={setNode} style={{ maxHeight, overflowY: 'auto' }} {...props}>
      <SystemColumnHeaderMenu {...props} />
    </GridColumnMenuContainer>
  );
}

export function SystemColumnHeaderMenu({ colDef }: GridColumnMenuProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const pinnedColumns = gridPinnedColumnsSelector(apiRef);
  const isPinnedLeft = pinnedColumns.left?.includes(colDef.field) ?? false;

  // Don't allow unpinning the checkbox column
  const canTogglePin = colDef.field !== GRID_CHECKBOX_SELECTION_FIELD;

  const handlePinClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isPinnedLeft) {
      apiRef.current.setPinnedColumns({
        ...pinnedColumns,
        left: pinnedColumns.left?.filter(field => field !== colDef.field),
      });
      return;
    }

    apiRef.current.setPinnedColumns({
      ...pinnedColumns,
      left: [...(pinnedColumns.left ?? []), colDef.field],
    });
  };

  return (
    <>
      {canTogglePin && (
        <MenuItem onClick={handlePinClick}>
          <ListItemIcon className={classes.deleteIcon}>
            {isPinnedLeft ? (
              <PinOff size={QUESTION_HEADER_ICON_SIZE} />
            ) : (
              <Pin size={QUESTION_HEADER_ICON_SIZE} />
            )}
          </ListItemIcon>
          <ListItemText>{isPinnedLeft ? 'Unpin column' : 'Pin column'}</ListItemText>
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          apiRef.current.setColumnVisibility(colDef.field, false);
        }}
      >
        <ListItemIcon className={classes.deleteIcon}>
          <EyeOff size={QUESTION_HEADER_ICON_SIZE} />
        </ListItemIcon>
        <ListItemText>Hide property</ListItemText>
      </MenuItem>
    </>
  );
}

export interface ColumnHeaderQuestion extends Pick<
  Apollo.ExtractionQuestion,
  | 'id'
  | 'inputFileFields'
  | 'questionText'
  | 'title'
  | '__typename'
  | 'type'
  | 'variant'
  | 'options'
  | 'mode'
> {
  inputs: Pick<Apollo.ExtractionQuestion, 'id' | 'title' | '__typename'>[];
}

interface StandardPropertyColumnHeaderMenuProps {
  colDef: GridColDef;
  hideMenu: (event: React.SyntheticEvent<Element, Event>) => void;
  standardQuestionId: string;
}

export function StandardPropertyColumnHeaderMenu({
  colDef,
  hideMenu,
  standardQuestionId,
}: StandardPropertyColumnHeaderMenuProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const { reportId } = useReportParams();
  const visibleDocumentIds = useVisibleDocumentIds();
  const { loading: startingColExtraction, startReportExtraction } = useStartReportExtraction();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const handleLockClick = useLockColumnHandler({
    filterModel,
    questionId: standardQuestionId,
    reportId,
  });
  const columnLockState = useColumnLockState(reportId, standardQuestionId, {
    fetchPolicy: 'cache-and-network',
    filterModel,
    nextFetchPolicy: 'cache-first',
  });

  const handleRunCoreAI = async (event: React.MouseEvent<Element, Event>) => {
    await startReportExtraction({
      variables: {
        input: {
          filterModel,
          filterTimeZone,
          questionId: standardQuestionId,
          reportId,
          visibleDocumentIds,
        },
      },
    });
    hideMenu(event);
  };

  const columnLockAction = getLockAction(columnLockState);

  return (
    <>
      <MenuItem
        onClick={event => {
          hideMenu(event);
          apiRef.current.setColumnVisibility(colDef.field, false);
        }}
      >
        <ListItemIcon className={classes.deleteIcon}>
          <EyeOff size={QUESTION_HEADER_ICON_SIZE} />
        </ListItemIcon>
        <ListItemText>Hide property</ListItemText>
      </MenuItem>
      <MenuItem disabled={startingColExtraction} onClick={handleRunCoreAI}>
        <ListItemIcon className={classes.deleteIcon}>
          <Sparkles size={QUESTION_HEADER_ICON_SIZE} />
        </ListItemIcon>
        <ListItemText>Run core AI property</ListItemText>
      </MenuItem>
      {columnLockState === 'empty' && (
        <MenuItem disabled>
          <ListItemIcon className={classes.deleteIcon}>
            <LockIcon action="empty" iconProps={{ size: QUESTION_HEADER_ICON_SIZE }} />
          </ListItemIcon>
          <ListItemText>{COLUMN_LOCK_EMPTY_LABEL}</ListItemText>
        </MenuItem>
      )}
      {columnLockAction && (
        <MenuItem
          onClick={event => {
            hideMenu(event);
            handleLockClick(columnLockAction);
          }}
        >
          <ListItemIcon className={classes.deleteIcon}>
            <LockIcon action={columnLockAction} iconProps={{ size: QUESTION_HEADER_ICON_SIZE }} />
          </ListItemIcon>
          <ListItemText>{COLUMN_LOCK_LABEL[columnLockAction]}</ListItemText>
        </MenuItem>
      )}
    </>
  );
}

interface QuestionColumnHeaderMenuBodyProps {
  colDef: GridColDef;
  context:
    | {
        apiRef: React.RefObject<GridApiPremium | null>;
        /**
         * Live grid filterModel — must be supplied by the caller via
         * `useGridSelector` so the lock affordances react to filter changes.
         */
        filterModel: GridFilterModel | undefined;
        reportId: string;
        type: 'report';
      }
    | {
        documentId: string;
        extractionJob: (Pick<Apollo.ExtractionJob, 'id' | '__typename'> & LockableJob) | null;
        type: 'document';
      };
  question: ColumnHeaderQuestion;
  onClose?: () => void;
  popperAnchor?: 'left-start' | 'right-start';
  showHideButton?: boolean;
}

interface QuestionFormState {
  questionText: string;
  questionTitle: string;
}

type ActiveMenu = 'inputs' | 'type' | 'mode' | null;

export function QuestionColumnHeaderMenu({
  colDef,
  hideMenu,
  popperAnchor = 'right-start',
}: ColumnHeaderMenuProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const { reportId } = useReportParams();
  const { extractionQuestion, loading } = useGetExtractionQuestionWithInputs(colDef.field);

  if (loading || !extractionQuestion)
    return (
      <div className={classes.paper}>
        <div className={classes.header}>
          <Skeleton className={classes.skeletonMenuButton} height={32} />
          <Skeleton height={32} variant="circular" width={32} />
        </div>
        <Divider />
        <div className={classes.container}>
          <Skeleton height={100} variant="rectangular" width="100%" />
        </div>
        <Divider />
        <div className={classes.container}>
          <Skeleton className={classes.skeletonMenuButton} height={32} />
        </div>
        <Divider />
        <div className={classes.container}>
          <Skeleton className={classes.skeletonButton} height={48} />
        </div>
      </div>
    );

  if (extractionQuestion.variant !== Apollo.ExtractionQuestionVariant.Custom)
    return (
      <StandardPropertyColumnHeaderMenu
        colDef={colDef}
        hideMenu={hideMenu}
        standardQuestionId={extractionQuestion.id}
      />
    );

  return (
    <QuestionColumnHeaderMenuBody
      colDef={colDef}
      context={{ apiRef, filterModel, reportId, type: 'report' }}
      popperAnchor={popperAnchor}
      question={extractionQuestion}
    />
  );
}

interface QuestionMenuBodyStyleProps {
  activeMenu: ActiveMenu;
}

export const questionMenuItemBorderRadius = 8;
const useQuestionMenuBodyStyles = makeStyles<QuestionMenuBodyStyleProps>()(
  (theme, { activeMenu }) => ({
    buttonRow: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    buttonTitle: {
      ...theme.appTypography.fieldLabel,
    },
    buttonsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
    deleteBody: {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      padding: theme.spacing(1),
      width: 400,
    },
    deleteButtonContainer: {
      display: 'flex',
      flexWrap: 'nowrap',
      gap: theme.spacing(2),
      width: '100%',
    },
    deletePropertyIcon: {
      marginTop: 32,
    },
    inputsMenuButton: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      backgroundColor: activeMenu === 'inputs' ? theme.appColors.hoverBackground : undefined,
    },
    menuButton: {
      alignItems: 'center',
      borderRadius: questionMenuItemBorderRadius,
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'left',
      minHeight: 32,
      padding: theme.spacing(0.5, 1),
      width: '100%',
    },
    modeMenuButton: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      backgroundColor: activeMenu === 'mode' ? theme.appColors.hoverBackground : undefined,
    },
    modeText: {
      fontSize: 12,
    },
    propertyName: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      borderRadius: questionMenuItemBorderRadius,
      flexGrow: 1,
      height: 32,
      padding: theme.spacing(0, 1),
    },
    sparkleIcon: {
      marginRight: theme.spacing(1),
    },
    tooltipWrapper: {
      display: 'flex',
      flexGrow: 1,
    },
    typeMenuButton: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      backgroundColor: activeMenu === 'type' ? theme.appColors.hoverBackground : undefined,
    },
    typeText: {
      fontSize: 12,
    },
  })
);

export function QuestionColumnHeaderMenuBody({
  colDef,
  context,
  onClose = doNothing,
  popperAnchor = 'right-start',
  question,
  showHideButton = true,
}: QuestionColumnHeaderMenuBodyProps): JSX.Element {
  const { classes } = useStyles();
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const [body, setBody] = useState<'question' | 'delete'>('question');
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const [promptEl, setPromptEl] = useState<HTMLTextAreaElement | null>(null);
  const { classes: questionMenuBodyClasses } = useQuestionMenuBodyStyles({ activeMenu });
  const { deleteExtractionQuestion, loading: deletingExtractionQuestion } =
    useDeleteExtractionQuestion();
  const { loading: startingExtraction, startExtractionJobsForDocuments } =
    useRunExtractionJobsForDocuments();
  const [typeButtonEl, setTypeButtonEl] = useState<HTMLElement | null>(null);
  const [inputsButtonEl, setInputsButtonEl] = useState<HTMLElement | null>(null);
  const [modeButtonEl, setModeButtonEl] = useState<HTMLElement | null>(null);
  const { updateQuestionTypeOptimistic } = useUpdateQuestionTypeOptimistic();
  const { updateQuestionModeOptimistic } = useUpdateQuestionModeOptimistic();
  const [updateExtractionQuestion] = Apollo.useUpdateExtractionQuestionMutation();
  const { setQuestionInputsOptimistic } = useSetQuestionInputsOptimistic(question.id);
  const debouncedUpdateExtractionQuestion = useDebouncedCallback(updateExtractionQuestion, 500);
  const { loading: startingColExtraction, startReportExtraction } = useStartReportExtraction();
  const client = useApolloClient();
  const [state, setState] = useState<QuestionFormState>({
    questionText: question.questionText,
    questionTitle: question.title,
  });
  const reportId = context.type === 'report' ? context.reportId : null;
  const liveFilterModel = context.type === 'report' ? context.filterModel : undefined;
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const handleLockColumn = useLockColumnHandler({
    filterModel: liveFilterModel,
    questionId: question.id,
    reportId,
  });
  const columnLockState = useColumnLockState(reportId, question.id, {
    fetchPolicy: 'cache-and-network',
    filterModel: liveFilterModel,
    nextFetchPolicy: 'cache-first',
  });
  const isAiMode = question.mode === Apollo.ExtractionQuestionMode.Ai;

  const pinnedColumns =
    context.type === 'report' ? gridPinnedColumnsSelector(context.apiRef) : { left: [], right: [] };
  const isPinnedLeft =
    context.type === 'report' && (pinnedColumns.left?.includes(colDef.field) ?? false);

  useEffect(() => {
    if (inputEl) inputEl.select();
  }, [inputEl]);

  const closeMenu = () => {
    onClose();
    if (context.type === 'report') context.apiRef.current?.hideColumnMenu();
  };

  const handlePinClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (context.type !== 'report') {
      return;
    }

    if (isPinnedLeft) {
      context.apiRef.current?.setPinnedColumns({
        ...pinnedColumns,
        left: pinnedColumns.left?.filter(field => field !== colDef.field),
      });
      return;
    }

    context.apiRef.current?.setPinnedColumns({
      ...pinnedColumns,
      left: [...(pinnedColumns.left ?? []), colDef.field],
    });
  };

  const handleExtraction = async () => {
    if (question.mode === Apollo.ExtractionQuestionMode.Manual) {
      closeMenu();
      return;
    }

    switch (context.type) {
      case 'report': {
        await startReportExtraction({
          variables: {
            input: {
              filterModel: liveFilterModel,
              filterTimeZone,
              questionId: question.id,
              reportId: context.reportId,
              visibleDocumentIds: getVisibleDocumentIds(context.apiRef.current),
            },
          },
        });
        closeMenu();
        break;
      }
      case 'document': {
        await startExtractionJobsForDocuments({
          variables: {
            input: {
              documentIds: [context.documentId],
              // force: true needed for document context to ensure play button always re-runs specific question.
              force: true,
              questionIds: [question.id],
            },
          },
        });
        closeMenu();
      }
    }
  };

  const editorLockAction = context.type === 'report' ? getLockAction(columnLockState) : null;
  const lockTitle = editorLockAction
    ? COLUMN_LOCK_LABEL[editorLockAction]
    : COLUMN_LOCK_EMPTY_LABEL;

  const handleLockClick = async (action: LockAction) => {
    handleLockColumn(action);
  };

  switch (body) {
    case 'question': {
      return (
        <>
          <div className={classes.paper}>
            <div className={classes.header}>
              <InputBase
                className={questionMenuBodyClasses.propertyName}
                data-cy={Select.report.aiPropertyNameInput}
                inputRef={setInputEl}
                placeholder="Type property name..."
                value={state.questionTitle}
                onChange={async e => {
                  setState(prev => ({ ...prev, questionTitle: e.target.value }));
                  if (context.type === 'report')
                    context.apiRef.current?.updateColumns([
                      {
                        ...colDef,
                        headerName: e.target.value,
                      },
                    ]);
                  patchExtractionColumnOptionInCache(client.cache, {
                    patch: { displayName: e.target.value },
                    questionId: question.id,
                    reportId,
                  });
                  await debouncedUpdateExtractionQuestion({
                    refetchQueries: [
                      {
                        query: Apollo.GetExtractionQuestionWithDependenciesDocument,
                        variables: {
                          id: question.id,
                        } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
                      },
                    ],
                    variables: {
                      input: {
                        props: {
                          title: e.target.value.trim(),
                        },
                        questionId: question.id,
                      },
                    },
                  });
                }}
                onKeyDown={e => {
                  if ((e.key === 'Tab' && !e.shiftKey) || e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    promptEl?.focus();
                  }
                }}
              />
              {context.type === 'report' && (
                <AlineTooltip
                  arrow
                  placement="top"
                  title={isPinnedLeft ? 'Unpin column' : 'Pin column'}
                >
                  <div>
                    <IconButton size="small" onClick={handlePinClick}>
                      {isPinnedLeft ? (
                        <PinOff size={QUESTION_HEADER_ICON_SIZE} />
                      ) : (
                        <Pin size={QUESTION_HEADER_ICON_SIZE} />
                      )}
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              {showHideButton && (
                <AlineTooltip arrow placement="top" title="Hide property">
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (context.type === 'report')
                          context.apiRef.current?.setColumnVisibility(colDef.field, false);
                        closeMenu();
                      }}
                    >
                      <EyeOff size={QUESTION_HEADER_ICON_SIZE} />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              {context.type === 'report' && editorLockAction && (
                <AlineTooltip arrow placement="top" title={lockTitle}>
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleLockClick(editorLockAction);
                        closeMenu();
                      }}
                    >
                      <LockIcon
                        action={editorLockAction}
                        iconProps={{ size: QUESTION_HEADER_ICON_SIZE }}
                      />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              {context.type === 'report' && !editorLockAction && (
                <AlineTooltip arrow placement="top" title={COLUMN_LOCK_EMPTY_LABEL}>
                  <div>
                    <IconButton disabled size="small">
                      <LockIcon action="empty" iconProps={{ size: QUESTION_HEADER_ICON_SIZE }} />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              <AlineTooltip arrow placement="top" title="Delete property">
                <div>
                  <IconButton size="small" onClick={() => setBody('delete')}>
                    <Trash2 size={QUESTION_HEADER_ICON_SIZE} />
                  </IconButton>
                </div>
              </AlineTooltip>
            </div>
            <Divider />
            <div className={classes.container}>
              <AlineTextarea
                ref={setPromptEl}
                data-cy={Select.report.promptText}
                maxRows={5}
                minRows={5}
                placeholder="Type an AI prompt (e.g., What is the effective date)..."
                value={state.questionText}
                onChange={async e => {
                  setState(prev => ({ ...prev, questionText: e.target.value }));
                  await debouncedUpdateExtractionQuestion({
                    refetchQueries: [
                      {
                        query: Apollo.GetExtractionQuestionWithDependenciesDocument,
                        variables: {
                          id: question.id,
                        } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
                      },
                    ],
                    variables: {
                      input: {
                        props: {
                          questionText: e.target.value.trim(),
                        },
                        questionId: question.id,
                      },
                    },
                  });
                }}
                onKeyDown={e => {
                  if (e.key === 'Tab' && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    inputEl?.focus();
                  }
                }}
              />
            </div>
            <Divider />
            <div className={questionMenuBodyClasses.buttonsContainer}>
              <ButtonBase
                ref={setModeButtonEl}
                className={clsx(
                  questionMenuBodyClasses.menuButton,
                  questionMenuBodyClasses.modeMenuButton
                )}
                onClick={() => setActiveMenu('mode')}
              >
                <Typography className={questionMenuBodyClasses.buttonTitle}>Mode</Typography>
                <Spacer />
                <Typography className={questionMenuBodyClasses.modeText}>
                  {question.mode === Apollo.ExtractionQuestionMode.Ai ? 'AI' : 'Manual Entry'}
                </Typography>
                <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
              </ButtonBase>
              <ButtonBase
                ref={setTypeButtonEl}
                className={clsx(
                  questionMenuBodyClasses.menuButton,
                  questionMenuBodyClasses.typeMenuButton
                )}
                onClick={() => setActiveMenu('type')}
              >
                <Typography className={questionMenuBodyClasses.buttonTitle}>Output</Typography>
                <Spacer />
                <ExtractionQuestionTypeIcon editing type={question.type} />
                <Typography className={questionMenuBodyClasses.typeText}>
                  {question.type}
                </Typography>
                <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
              </ButtonBase>
              <ExtractionQuestionOptionsManager question={question} />
              {isAiMode && (
                <ButtonBase
                  ref={setInputsButtonEl}
                  className={clsx(
                    questionMenuBodyClasses.menuButton,
                    questionMenuBodyClasses.inputsMenuButton
                  )}
                  onClick={() => setActiveMenu('inputs')}
                >
                  <Typography className={questionMenuBodyClasses.buttonTitle}>Inputs</Typography>
                  <Spacer />
                  <QuestionInputChips
                    inputFileFields={question.inputFileFields ?? []}
                    inputs={question.inputs}
                  />
                  <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
                </ButtonBase>
              )}
            </div>
            {isAiMode && (
              <>
                <Divider />
                <div className={classes.container}>
                  <div className={questionMenuBodyClasses.buttonRow}>
                    <AlineButton
                      fullWidth
                      color="primary"
                      data-cy={Select.report.generateWithAlineAIButton}
                      loading={startingColExtraction || startingExtraction}
                      variant="contained"
                      onClick={handleExtraction}
                    >
                      <Sparkles
                        className={questionMenuBodyClasses.sparkleIcon}
                        color="currentColor"
                        size={QUESTION_HEADER_ICON_SIZE}
                      />
                      Run Aline AI
                    </AlineButton>
                  </div>
                </div>
              </>
            )}
            <ExtractionModePopper
              popperProps={{
                anchorEl: modeButtonEl,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 12],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 16,
                    },
                  },
                ],
                open: activeMenu === 'mode',
                placement: popperAnchor,
              }}
              question={question}
              onChangeMode={async mode => {
                setActiveMenu(null);
                await updateQuestionModeOptimistic(question.id, mode);
              }}
              onClose={() => setActiveMenu(null)}
            />
            <ExtractionQuestionTypePopper
              popperProps={{
                anchorEl: typeButtonEl,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 12],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 16,
                    },
                  },
                ],
                open: activeMenu === 'type',
                placement: popperAnchor,
              }}
              selectedType={question.type}
              onChangeType={async type => {
                setActiveMenu(null);
                const newDataType = questionTypeToReportColumnDataType(type);
                if (context.type === 'report') {
                  const columnSpecificOpts = getColumnSpecificOpts({
                    __typename: 'ExtractionReportColumn',
                    dataType: newDataType,
                    displayName: colDef.headerName ?? '',
                    isGroupable: true,
                    questionId: question.id,
                  });
                  context.apiRef.current?.updateColumns([
                    {
                      ...colDef,
                      ...columnSpecificOpts,
                    },
                  ]);
                }
                patchExtractionColumnOptionInCache(client.cache, {
                  patch: { dataType: newDataType },
                  questionId: question.id,
                  reportId,
                });
                await updateQuestionTypeOptimistic(question.id, type);
              }}
              onClose={() => setActiveMenu(null)}
            />
            <ExtractionQuestionInputsPopper
              apiRef={context.type === 'report' ? context.apiRef : null}
              inputFileFields={question.inputFileFields ?? []}
              inputQuestionIds={question.inputs.map(input => input.id)}
              popperProps={{
                anchorEl: inputsButtonEl,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 12],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 16,
                    },
                  },
                ],
                open: activeMenu === 'inputs',
                placement: popperAnchor,
              }}
              question={question}
              onChangeSelectedInputs={async ({ inputFileFields, inputQuestionIds }) => {
                await setQuestionInputsOptimistic({
                  variables: {
                    input: {
                      inputFileFields,
                      inputQuestionIds,
                      questionId: question.id,
                    },
                  },
                });
              }}
              onClose={() => setActiveMenu(null)}
            />
          </div>
        </>
      );
    }
    case 'delete': {
      return (
        <div className={questionMenuBodyClasses.deleteBody}>
          <Trash2
            className={questionMenuBodyClasses.deletePropertyIcon}
            size={DELETE_PROPERTY_ICON_SIZE}
          />
          <Typography align="center" variant="subtitle1">
            Are you sure you want to delete this AI property?
          </Typography>
          <Typography align="center" color="textSecondary">
            Deleting will remove the AI property from all reports. This action cannot be undone.
          </Typography>
          <div className={questionMenuBodyClasses.deleteButtonContainer}>
            <AlineButton
              fullWidth
              color="primary"
              variant="outlined"
              onClick={() => setBody('question')}
            >
              Go Back
            </AlineButton>
            <AlineButton
              fullWidth
              color="primary"
              loading={deletingExtractionQuestion}
              variant="contained"
              onClick={async () => {
                const { data } = await deleteExtractionQuestion(colDef.field);
                if (data) {
                  closeMenu();
                  if (context.type === 'report')
                    context.apiRef.current?.setColumnVisibility(colDef.field, false);
                }
              }}
            >
              Delete
            </AlineButton>
          </div>
        </div>
      );
    }
  }
}

void gql`
  mutation UpdateQuestionType($input: UpdateExtractionQuestionInput!) {
    updateExtractionQuestion(input: $input) {
      __typename
      id
      contentHash
      type
    }
  }
`;

function useUpdateQuestionTypeOptimistic() {
  const [updateExtractionQuestionType] = Apollo.useUpdateQuestionTypeMutation();
  const updateQuestionTypeOptimistic = useCallback(
    async (questionId: string, type: Apollo.ExtractionQuestionType) => {
      await updateExtractionQuestionType({
        optimisticResponse: {
          __typename: 'Mutation',
          updateExtractionQuestion: {
            __typename: 'ExtractionQuestion',
            contentHash: uuidv4(),
            id: questionId,
            type,
          },
        },
        refetchQueries: [
          {
            query: Apollo.GetExtractionQuestionWithDependenciesDocument,
            variables: {
              id: questionId,
            } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
          },
        ],
        variables: {
          input: { props: { type }, questionId },
        },
      });
    },
    [updateExtractionQuestionType]
  );

  return { updateQuestionTypeOptimistic };
}

void gql`
  mutation UpdateQuestionMode($input: UpdateExtractionQuestionInput!) {
    updateExtractionQuestion(input: $input) {
      __typename
      id
      mode
    }
  }
`;

function useUpdateQuestionModeOptimistic() {
  const [updateExtractionQuestionMode] = Apollo.useUpdateQuestionModeMutation();
  const updateQuestionModeOptimistic = useCallback(
    async (questionId: string, mode: Apollo.ExtractionQuestionMode) => {
      await updateExtractionQuestionMode({
        optimisticResponse: {
          __typename: 'Mutation',
          updateExtractionQuestion: {
            __typename: 'ExtractionQuestion',
            id: questionId,
            mode,
          },
        },
        variables: {
          input: { props: { mode }, questionId },
        },
      });
    },
    [updateExtractionQuestionMode]
  );

  return { updateQuestionModeOptimistic };
}

/**
 * Shared lock-column handler for AI property and standard property header
 * menus. Wraps the toggle mutation in the lock-confirmation modal and
 * normalizes refetch / error / snackbar handling across both call sites.
 */
function useLockColumnHandler({
  filterModel,
  questionId,
  reportId,
}: {
  /**
   * Live grid filterModel (subscribed via `useGridSelector` in the caller) so
   * the mutation scopes to the rows the user is actually looking at.
   */
  filterModel: GridFilterModel | undefined;
  questionId: string;
  reportId: string | null | undefined;
}): (action: LockAction) => void {
  const [toggleExtractionJobsLock] = Apollo.useToggleExtractionJobsLockMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { requestLockConfirmation } = useLockConfirmation();

  return (action: LockAction) => {
    if (!reportId) return;
    requestLockConfirmation({
      action,
      onConfirm: async () => {
        try {
          await toggleExtractionJobsLock({
            refetchQueries: [
              {
                query: Apollo.GetQuestionLockStatusDocument,
                variables: { questionId, reportId },
              },
            ],
            variables: {
              input: {
                filterModel: filterModel ?? null,
                isLocked: action === 'lock',
                questionId,
                reportId,
                runAllJobs: true,
              },
            },
          });
        } catch (err) {
          Sentry.captureException(err, { tags: { feature: 'report-column-lock' } });
          enqueueSnackbar('Failed to update column lock. Please try again.', { variant: 'error' });
        }
      },
      target: 'column',
    });
  };
}
